import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { Package, PackageCheck, Users, ShoppingCart, Clock, CheckCircle2, IndianRupee, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const statusColors = {
  New: "#3b82f6", Confirmed: "#6366f1", Processing: "#f59e0b",
  Shipped: "#a855f7", Delivered: "#22c55e", Cancelled: "#ef4444",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats(false));
  }, []);

  if (!stats) return <div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-gold" /></div>;

  const cards = [
    { label: "Total Sales", value: formatINR(stats.total_sales), icon: IndianRupee, accent: true },
    { label: "Total Orders", value: stats.total_orders, icon: ShoppingCart },
    { label: "Pending Orders", value: stats.pending_orders, icon: Clock },
    { label: "Completed", value: stats.completed_orders, icon: CheckCircle2 },
    { label: "Total Products", value: stats.total_products, icon: Package },
    { label: "Active Products", value: stats.active_products, icon: PackageCheck },
    { label: "Customers", value: stats.total_customers, icon: Users },
  ];

  const chartData = Object.entries(stats.status_counts).map(([name, value]) => ({ name, value }));

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl lg:text-4xl text-obsidian mb-1">Dashboard</h1>
      <p className="text-sm text-muted-foreground mb-8">Overview of your store performance</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className={`p-5 border ${c.accent ? "bg-obsidian text-white border-obsidian" : "bg-white border-black/10"}`} data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <c.icon className={`w-5 h-5 ${c.accent ? "text-gold" : "text-gold-dark"}`} />
            </div>
            <p className={`text-2xl font-bold ${c.accent ? "text-white" : "text-obsidian"}`}>{c.value}</p>
            <p className={`text-xs mt-1 ${c.accent ? "text-white/60" : "text-muted-foreground"}`}>{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 p-6">
          <h2 className="font-serif text-2xl mb-4">Orders by Status</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {chartData.map((e) => <Cell key={e.name} fill={statusColors[e.name] || "#999"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Recent Orders</h2>
            <Link to="/admin/orders" className="text-sm text-gold-dark font-semibold hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {stats.recent_orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
            {stats.recent_orders.map((o) => (
              <Link key={o.id} to="/admin/orders" className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0 hover:bg-secondary/50 -mx-2 px-2 transition-colors">
                <div>
                  <p className="text-sm font-semibold text-obsidian">#{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{o.customer.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{formatINR(o.total)}</p>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: statusColors[o.status] }}>{o.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
